import React, { useCallback, useEffect, useState } from 'react';

// Style
import './LibrariesCompounds.scss';
import 'pages/Main/SectionHighlight.scss';

// Router
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// Lib
import { shallowEqual } from 'react-redux';
import { getValueSize } from 'lib/helpers';
import { useAppDispatch, useAppSelector } from 'lib/hooks/useDispatch';

// Components
import List from './components/List';
import Header from './components/Header';
import Button from 'components/UiKit/Button';
import HeaderList from './components/HeaderList';
import ScrollButton from 'components/ScrollButton';
import ErrorBoundary from 'components/ErrorBoundary';
import PaginationView from 'components/PaginationView';
import PricesOnAsking from 'components/PricesOnAsking';
import DebounceInput from 'components/UiKit/DebounceInput';
import FormatOptionLabel from 'components/FormatOptionLabel';
import CustomMessage from 'components/Messages/CustomMessage';

// Actions
import { showLoader } from 'reducers/loader';
import * as actionsCreator from './reducer';
import { showMessage } from 'reducers/messages';

// Type
import { TContent } from 'pages/LibrariesCompounds/reducer';

type TFuncArgs = {
	[index: string]: string | number;
}

// Constants
export const optionsSize = [
	{ label: '10', value: 10 },
	{ label: '20', value: 20 },
	{ label: '30', value: 30 },
	{ label: '40', value: 40 },
	{ label: '50', value: 50 },
	{ label: '60', value: 60 }
];
const messageNotEmptyValue = {
	type: 'info',
	text: 'Value shouldn\'t be null'
};

export const LibrariesCompounds: React.FC = () => {
	const urlParam = useParams();
	const { state } = useLocation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const librariesCompounds = useAppSelector(
		(store) => store.librariesCompounds,
		shallowEqual
	);
	const data = librariesCompounds.data;
	const dataSearch = librariesCompounds.dataSearch;
	const toggleFilter = librariesCompounds.toggleFilter;
	const number = data?.number;

	const { id } = urlParam;
	const inputDebounce: HTMLInputElement | null = document.querySelector('.input-debounce__list');

	const [isShow, setIsShow] = useState<boolean>(false);
	const [goTo, setGoTo] = useState<number>(1);
	const [newData, setNewData] = useState<TContent[]>([]);
	const [isRecent, setIsRecent] = useState<boolean>(false);
	const [isSuggest, setIsSuggest] = useState<boolean>(false);

	const size = data.size + '';
	const content = data.content;
	const totalPages = data.totalPages;
	const value = librariesCompounds.value;
	const loading = librariesCompounds.loading;

	useEffect(() => {
		return () => {
			setIsSuggest(false);
			dispatch(actionsCreator.handlerClearState());
		};
	}, []);

	useEffect(() => {
		if (id) {
			dispatch(actionsCreator.getLibrariesCompoundsIds(id));
			dispatch(actionsCreator.getLibrariesCompounds(id));
		}
	}, [id]);

	useEffect(() => {
		const result = value ? dataSearch.content : content;
		setNewData(result || []);
	}, [value, content, dataSearch]);

	const onChangeInput = (
		e: React.ChangeEvent<HTMLInputElement>
	): void => {
		const value = e.target.value ? e?.target?.value : '';

		setIsSuggest(!!value);
		if (!value) {
			setIsRecent(false);
		}
		dispatch(actionsCreator.searchLibrariesCompounds({
			value: value,
			cutNumber: id
		}));

		if (inputDebounce) {
			inputDebounce.style.display = 'block';
		}
	};

	const handlerSubmit = () => {
		if (!value?.length) {
			dispatch(showMessage(messageNotEmptyValue));
			return false;
		}

		dispatch(actionsCreator.updateData());
		if (inputDebounce) {
			inputDebounce.style.display = 'none';
		}

		setIsShow(true);
	};

	const handlerClickLibrary = () => {
		navigate(`/libraries-product/${id}`);
	};

	const handleKeyDown = useCallback((
		e: React.KeyboardEvent<HTMLInputElement>
	): void | boolean => {
		if (e.key === 'Enter') {
			if (!value?.length) {
				dispatch(showMessage(messageNotEmptyValue));
				return false;
			}

			e.preventDefault();
			setIsShow(true);
			setIsSuggest(true);
			dispatch(actionsCreator.updateData());

			if (inputDebounce) {
				inputDebounce.style.display = 'none';
			}
		}
	}, [
		value,
		content,
		isSuggest,
		inputDebounce
	]);

	const leftIconAction = useCallback(() => {
		if (!value?.length) {
			dispatch(showMessage(messageNotEmptyValue));
			return false;
		}

		setIsSuggest(true);
		setNewData(dataSearch?.content || []);

		if (inputDebounce) {
			inputDebounce.style.display = 'none';
		}
	}, [
		value,
		content,
		isSuggest,
		inputDebounce
	]);

	const handlerClick = (value: string, step: number = 0) => {
		dispatch(actionsCreator.searchLibrariesCompounds({
			cutNumber: id,
			value: value,
			step: step
		}));

		setIsRecent(true);
	};

	const handlerShowAll = () => {
		dispatch(actionsCreator.getLibrariesCompounds(id));

		setIsShow(false);
	};

	const handlerChangeGoTo = (e: React.ChangeEvent<HTMLInputElement>) => {
		const number = +e?.target?.value > 0 ? +e?.target?.value : 1;

		if (number <= (totalPages || 0)) {
			setGoTo(number);
		} else {
			dispatch(
				showMessage({
					type: 'info',
					text: 'This is the last record'
				})
			);
		}
	};

	const getRequestPagination = (number: number) => {
		dispatch(actionsCreator.handlerChangePage(number));

		if (!!value) {
			handlerClick(value, number);
		} else {
			dispatch(actionsCreator.getLibrariesCompounds(id));
		}

		dispatch(showLoader());
	};

	const handlerChangeSize = (data: TFuncArgs) => {
		dispatch(actionsCreator.handlerChangeSize(data?.value));

		dispatch(actionsCreator.getLibrariesCompounds(id));
	};

	const onNextPressLast = () => {
		getRequestPagination((totalPages || 0) - 1);
	};

	const onPrevPressLast = () => {
		getRequestPagination(0);
	};

	const onPagePress = (number: number) => {
		getRequestPagination(number - 1);
	};

	const onKeyPress = () => {
		dispatch(actionsCreator.handlerChangePage(goTo - 1));

		if (!!value) {
			handlerClick(value, goTo - 1);
		} else {
			dispatch(actionsCreator.getLibrariesCompounds(id));
		}

		dispatch(showLoader());
	};

	const valueSize = getValueSize({
		opt: +size,
		options: optionsSize
	});

	return (
		<ErrorBoundary>
			<ScrollButton />

			<div className="libraries-compounds relative">
				{toggleFilter && <div className="libraries-compounds__filter-mobile">
					<HeaderList
						isModal
						id={id || ''}
						state={state}
						className="libraries-compounds__filter-mobile-header"
					/>
				</div>}

				<div className="wrapper relative">
					<Header
						id={id || ''}
						state={state}
						totalElements={state?.compoundsCount || 0}
					/>

					<div className="libraries-compounds__search flex flex-ai-center flex-jc-between mb40">
						<div className="flex flex-ai-center libraries-compounds__search-wrapper flex-jc-start flex-direction-column-mobile">
							<DebounceInput
								isWhite
								isLeftIcon
								minLength={3}
								loading={loading}
								isSuggest={isSuggest}
								debounceTimeout={300}
								callback={handlerClick}
								onChange={onChangeInput}
								style={{ width: '34rem' }}
								onKeyDown={handleKeyDown}
								suggest={dataSearch.content}
								Children={FormatOptionLabel}
								leftIconAction={leftIconAction}
								placeholder="Virtid, ID, CAS, Index"
								classNameWrapper="input-debounce__white"
							/>

							<Button
								variable="info"
								color="white"
								label="Search"
								type="button"
								typePadding="middle"
								onPress={handlerSubmit}
								className="ml30 search-screen__search-btn"
							/>

							{isShow && <Button
								variable="outline"
								color="white"
								label="Show all"
								className="ml10"
								onPress={handlerShowAll}
							/>}
						</div>

						<Button
							color="white"
							variable="outline"
							label="More about library"
							onPress={handlerClickLibrary}
							className="libraries-compounds__more-about-library"
						/>
					</div>

					{((totalPages || 0) > 1) && <PaginationView
						isWhite
						isShowSelect

						goTo={goTo}
						onKeyPress={onKeyPress}
						handlerSubmitGoTo={onKeyPress}
						handlerChangeGoTo={handlerChangeGoTo}

						number={number}
						totalPages={totalPages}
						onPagePress={onPagePress}
						onNextPressLast={onNextPressLast}
						onPrevPressLast={onPrevPressLast}

						valueSize={valueSize}
						optionsSize={optionsSize}
						handlerChangeSize={handlerChangeSize}
					/>}

					<div className="libraries-compounds__content mt30">
						<HeaderList
							id={id || ''}
							state={state}
						/>

						<List
							size={size}
							page={number}
							loading={loading}
							data={newData || []}
						/>
					</div>

					{!!newData?.length && <div className="pt15 flex flex-ai-center flex-jc-between">
						{((totalPages || 0) > 1) && <PaginationView
							isWhite

							children={<PricesOnAsking />}

							goTo={goTo}
							onKeyPress={onKeyPress}
							handlerSubmitGoTo={onKeyPress}
							handlerChangeGoTo={handlerChangeGoTo}

							number={number}
							totalPages={totalPages}
							onPagePress={onPagePress}
							onNextPressLast={onNextPressLast}
							onPrevPressLast={onPrevPressLast}

							valueSize={valueSize}
							optionsSize={optionsSize}
							handlerChangeSize={handlerChangeSize}
						/>}
					</div>}

					{newData?.length
						? <CustomMessage
							title="Notice"
							className="mt30"
							text="Due to the dynamic change of the Restricted Product list, if the above material is included in the Restricted Product list or regulated as controlled material by the government, we will not sell it."
						/>
						: null}
				</div>
			</div>
		</ErrorBoundary>
	);
};

export default LibrariesCompounds;
